import {
  Button,
  Drawer,
  Empty,
  Image,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Spin,
  Tag,
  Typography,
  Upload,
  message,
} from "antd"
import {
  CheckCircleFilled,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileOutlined,
  FolderOpenOutlined,
  InboxOutlined,
  LoadingOutlined,
} from "@ant-design/icons"
import { useEffect, useRef, useState } from "react"
import {
  deleteObject,
  getDownloadURL,
  getMetadata,
  getStorage,
  listAll,
  ref,
  uploadBytesResumable,
} from "firebase/storage"
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf"
import moment from "moment"

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js"

const { Text } = Typography
const { Dragger } = Upload

// ── PDF preview modal ────────────────────────────────────────────────────────
function PDFPreviewModal({ url, name, onClose }) {
  const canvasRef = useRef(null)
  const [imgSrc, setImgSrc] = useState(null)
  const [rendering, setRendering] = useState(true)

  useEffect(() => {
    if (!url) return
    let cancelled = false
    setRendering(true)
    setImgSrc(null)

    const render = async () => {
      try {
        const pdf = await pdfjsLib.getDocument(url).promise
        const page = await pdf.getPage(1)
        const viewport = page.getViewport({ scale: 1.8 })
        const canvas = canvasRef.current
        if (!canvas || cancelled) return
        canvas.width = viewport.width
        canvas.height = viewport.height
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise
        if (!cancelled) setImgSrc(canvas.toDataURL("image/jpeg"))
      } catch (err) {
        console.error("PDF render error:", err)
        if (!cancelled) message.error("Could not render PDF preview")
      } finally {
        if (!cancelled) setRendering(false)
      }
    }

    render()
    return () => { cancelled = true }
  }, [url])

  return (
    <>
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <Modal
        open={!!url}
        onCancel={onClose}
        footer={null}
        width={860}
        centered
        title={<Text ellipsis style={{ maxWidth: 760 }}>{name}</Text>}
        destroyOnClose>
        {rendering ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin tip="Rendering PDF…" />
          </div>
        ) : imgSrc ? (
          <Image src={imgSrc} alt="PDF preview" preview={false} style={{ width: "100%" }} />
        ) : (
          <Empty description="Could not render this file" />
        )}
      </Modal>
    </>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const isPDF = (name) => name?.toLowerCase().endsWith(".pdf")

const formatSize = (bytes) => {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── Upload progress card ─────────────────────────────────────────────────────
function UploadCard({ fileName, progress, done }) {
  return (
    <div
      style={{
        padding: "12px 16px",
        background: done ? "#f6ffed" : "#e6f4ff",
        borderRadius: 10,
        border: `1px solid ${done ? "#b7eb8f" : "#bae0ff"}`,
        marginBottom: 12,
        transition: "background 0.3s",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: done ? 0 : 8 }}>
        {done ? (
          <CheckCircleFilled style={{ color: "#52c41a", fontSize: 16 }} />
        ) : (
          <LoadingOutlined style={{ color: "#1677ff", fontSize: 16 }} />
        )}
        <Text style={{ fontSize: 13, flex: 1 }} ellipsis>
          {done ? `"${fileName}" uploaded!` : `Uploading "${fileName}"…`}
        </Text>
        {!done && (
          <Text style={{ fontSize: 12, color: "#1677ff", fontWeight: 600 }}>{progress}%</Text>
        )}
      </div>
      {!done && (
        <Progress
          percent={progress}
          status="active"
          size="small"
          showInfo={false}
          strokeColor={{ from: "#1677ff", to: "#52c41a" }}
        />
      )}
    </div>
  )
}

// ── Main drawer ──────────────────────────────────────────────────────────────
export default function PlateFilesDrawer({ code, open, onClose }) {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewName, setPreviewName] = useState(null)
  const [viewLoading, setViewLoading] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const [uploadState, setUploadState] = useState(null) // { name, progress, done }

  const loadFiles = () => {
    if (!code) return
    setLoading(true)
    const storage = getStorage()
    listAll(ref(storage, `vivaPlates/${code}`))
      .then((res) => {
        const validItems = res.items.filter((f) => !f.name.endsWith(".keep"))
        return Promise.all(
          validItems.map((fileRef) =>
            getMetadata(fileRef).then((meta) => ({
              name: fileRef.name,
              path: fileRef.fullPath,
              updated: meta.updated,
              size: meta.size,
            }))
          )
        )
      })
      .then((results) =>
        setFiles(results.sort((a, b) => new Date(b.updated) - new Date(a.updated)))
      )
      .catch((err) => { console.error(err); message.error("Failed to load plate files") })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!open || !code) return
    setFiles([])
    loadFiles()
  }, [open, code])

  const getUrl = (path) => getDownloadURL(ref(getStorage(), path))

  const handleDownload = (path, name) => {
    getUrl(path)
      .then((url) => {
        const a = document.createElement("a")
        a.href = url
        a.download = name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      })
      .catch(() => message.error("Failed to download file"))
  }

  const handleView = async (path, name) => {
    setViewLoading(path)
    try {
      const url = await getUrl(path)
      setPreviewUrl(url)
      setPreviewName(name)
    } catch {
      message.error("Failed to load preview")
    } finally {
      setViewLoading(null)
    }
  }

  const handleDelete = async (path, name) => {
    setDeleting(path)
    try {
      await deleteObject(ref(getStorage(), path))
      setFiles((prev) => prev.filter((f) => f.path !== path))
      message.success(`"${name}" deleted.`)
    } catch (err) {
      console.error(err)
      message.error("Failed to delete file")
    } finally {
      setDeleting(null)
    }
  }

  const handleUpload = (file) => {
    const storage = getStorage()
    const fileRef = ref(storage, `vivaPlates/${code}/${file.name}`)
    const task = uploadBytesResumable(fileRef, file)

    setUploadState({ name: file.name, progress: 0, done: false })

    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
        setUploadState((prev) => ({ ...prev, progress: pct }))
      },
      (err) => {
        console.error(err)
        message.error("Upload failed")
        setUploadState(null)
      },
      async () => {
        setUploadState((prev) => ({ ...prev, progress: 100, done: true }))
        // Inject the new file directly — don't rely on listAll which may lag
        try {
          const meta = await getMetadata(fileRef)
          const newFile = {
            name: fileRef.name,
            path: fileRef.fullPath,
            updated: meta.updated,
            size: meta.size,
          }
          setFiles((prev) => [newFile, ...prev.filter((f) => f.name !== newFile.name)])
        } catch (err) {
          console.error(err)
        }
        setTimeout(() => setUploadState(null), 2500)
      }
    )

    return false
  }

  return (
    <>
      <Drawer
        title={
          <Space>
            <FolderOpenOutlined style={{ color: "#1677ff" }} />
            <Text strong>Plate files — {code}</Text>
          </Space>
        }
        width={500}
        placement="right"
        open={open}
        onClose={onClose}
        destroyOnClose>

        {/* ── Drop zone ── */}
        <div style={{ height: 100, marginBottom: 16 }}>
          <Dragger
            beforeUpload={handleUpload}
            showUploadList={false}
            multiple={false}
            style={{ height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <InboxOutlined style={{ fontSize: 22, color: "#1677ff" }} />
              <div>
                <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>Click or drag a file to upload</p>
                <p style={{ margin: 0, fontSize: 11, color: "#999" }}>vivaPlates/{code}/</p>
              </div>
            </div>
          </Dragger>
        </div>

        {/* ── Upload progress card ── */}
        {uploadState && (
          <UploadCard
            fileName={uploadState.name}
            progress={uploadState.progress}
            done={uploadState.done}
          />
        )}

        {/* ── File list ── */}
        {loading ? (
          <div style={{ textAlign: "center", paddingTop: 40 }}>
            <Spin tip="Loading files…" />
          </div>
        ) : files.length === 0 ? (
          <Empty description="No files yet — upload one above" />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size="small">
            {files.map((file) => (
              <div
                key={file.path}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  background: "#fafafa",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}>
                <FileOutlined style={{ fontSize: 20, color: "#1677ff", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    ellipsis={{ tooltip: file.name }}
                    style={{ display: "block", fontWeight: 500, fontSize: 13 }}>
                    {file.name}
                  </Text>
                  <Space size={6}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {moment(file.updated).format("DD/MM/YYYY HH:mm")}
                    </Text>
                    {file.size && (
                      <Tag style={{ fontSize: 10, margin: 0 }}>{formatSize(file.size)}</Tag>
                    )}
                  </Space>
                </div>
                <Space size={2} style={{ flexShrink: 0 }}>
                  {isPDF(file.name) && (
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      loading={viewLoading === file.path}
                      onClick={() => handleView(file.path, file.name)}>
                      View
                    </Button>
                  )}
                  <Button
                    type="link"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(file.path, file.name)}>
                    Download
                  </Button>
                  <Popconfirm
                    title="Delete this file?"
                    description={`"${file.name}" will be permanently removed.`}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDelete(file.path, file.name)}>
                    <Button
                      type="link"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={deleting === file.path}
                    />
                  </Popconfirm>
                </Space>
              </div>
            ))}
          </Space>
        )}
      </Drawer>

      {previewUrl && (
        <PDFPreviewModal
          url={previewUrl}
          name={previewName}
          onClose={() => { setPreviewUrl(null); setPreviewName(null) }}
        />
      )}
    </>
  )
}
